import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DurationBar from './DurationBar';
import type { ReconcilerPlan } from '../../../../types/frontend';

export interface PlanPhaseProps {
  plan: ReconcilerPlan;
  maxMs: number;
}

const PlanPhase: React.FC<PlanPhaseProps> = ({ plan, maxMs }) => {
  const [expanded, setExpanded] = useState(false);

  if (plan.no_change) {
    return (
      <Box sx={{ py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Plan</Typography>
            <Typography variant="caption" color="text.secondary">
              — {plan.summary || 'no changes needed'}
            </Typography>
          </Box>
          <DurationBar ms={plan.duration_ms} maxMs={maxMs} />
        </Box>
      </Box>
    );
  }

  const totalActions = plan.groups.reduce((s, g) => s + g.actions.length, 0);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, exp) => setExpanded(exp)}
      sx={{
        bgcolor: 'transparent',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': { px: 0, minHeight: 'auto' },
        '& .MuiAccordionSummary-content': { my: 1 },
        '& .MuiAccordionDetails-root': { px: 0, pt: 0 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, mr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>Plan</Typography>
            <Typography variant="caption" color="text.secondary">
              {totalActions} action{totalActions !== 1 ? 's' : ''} across {plan.groups.length} group{plan.groups.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <DurationBar ms={plan.duration_ms} maxMs={maxMs} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ pl: 1 }}>
          {plan.groups.map((group, gi) => (
            <Box key={gi} sx={{ mb: 0.75 }}>
              {plan.groups.length > 1 && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.625rem' }}
                >
                  {group.actions.length} action{group.actions.length !== 1 ? 's' : ''}
                </Typography>
              )}
              {group.actions.map((action, ai) => (
                <Box key={ai} sx={{ py: 0.25, pl: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <FiberManualRecordIcon sx={{ fontSize: 6, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      {action.display_name}
                    </Typography>
                  </Box>
                  {action.description && (
                    <Typography variant="caption" color="text.disabled" sx={{ pl: 2.25, display: 'block', fontSize: '0.625rem' }}>
                      {action.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PlanPhase;
